/**
 * <nav-menu> — mega menu submenu positioner.
 *
 * Each <nav-menu> wraps a single child category (e.g. "Rods") inside the
 * mega-menu left column. When hovered, it positions its own
 * .disclosure__panel so that the panel (a) aligns with the hovered parent
 * when it fits the viewport, (b) shifts up just enough to stay in view
 * when it would overflow, and (c) falls back to an internal scroll only
 * when the panel is physically taller than the viewport.
 *
 * Desktop only (min-width: 1025px). On smaller viewports the theme uses a
 * different accordion pattern and this element is a no-op.
 */
class NavMenuPositioner extends HTMLElement {
  static SAFE_TOP = 16;
  static SAFE_BOTTOM = 16;
  static DESKTOP_MQ = '(min-width: 1025px)';

  constructor() {
    super();
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    this.cached = null;
  }

  connectedCallback() {
    this.panel = this.querySelector(':scope > details > .disclosure__panel');
    if (!this.panel) return;

    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('transitionend', this.handleTransitionEnd);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('transitionend', this.handleTransitionEnd);
  }

  /**
   * Apply the cached layout values to the panel synchronously. Used both
   * after the RAF computation and on transitionend — DetailsDisclosure's
   * handleTransitionEnd (main.js) wipes the panel's inline style on every
   * CSS transitionend of the panel, which would drop the panel back to its
   * CSS default position mid-hover. Re-applying synchronously here keeps
   * the panel under the cursor between mousedown and mouseup so the click
   * lands on the link instead of the slideshow underneath.
   */
  applyCached() {
    if (!this.cached || !this.panel) return;
    this.panel.style.top = this.cached.top;
    this.panel.style.maxHeight = this.cached.maxHeight;
    this.panel.style.overflowY = this.cached.overflowY;
  }

  /**
   * True when the desktop mega-menu layout is active. The mobile accordion
   * pattern does not use absolute positioning, so positioning is a no-op there.
   * @returns {boolean}
   */
  isDesktop() {
    return window.matchMedia(NavMenuPositioner.DESKTOP_MQ).matches;
  }

  /**
   * Position this element's .disclosure__panel so it fits the viewport:
   *   - Scenario 1 (default): anchor to parent (inline `top` cleared).
   *   - Scenario 2: if submenu bottom would exceed viewport bottom, shift
   *     up by the overflow amount.
   *   - Scenario 3: if the submenu is physically taller than the viewport,
   *     pin near viewport top and enable internal scroll (last-resort).
   * @returns {void}
   */
  position() {
    if (!this.panel) return;

    // Measure and apply inside a single RAF without first clearing inline
    // styles — clearing `top` would cause the panel to snap to its CSS
    // default for one frame, dropping it out from under the cursor on
    // mousedown (focusin → position() during a click). Use scrollHeight so
    // we don't need to clear maxHeight to get the natural content height.
    requestAnimationFrame(() => {
      const viewportH = window.innerHeight;
      const parentRect = this.getBoundingClientRect();
      const panelH = this.panel.scrollHeight;
      const { SAFE_TOP, SAFE_BOTTOM } = NavMenuPositioner;
      let top = '';
      let maxHeight = '';
      let overflowY = '';

      // Scenario 3 — taller than viewport: scroll fallback.
      if (panelH >= viewportH - SAFE_TOP - SAFE_BOTTOM) {
        top = `${SAFE_TOP - parentRect.top}px`;
        maxHeight = `${viewportH - SAFE_TOP - SAFE_BOTTOM}px`;
        overflowY = 'auto';
      } else {
        // Scenario 2 — would overflow viewport bottom: shift up.
        const overflow = (parentRect.top + panelH) - (viewportH - SAFE_BOTTOM);
        if (overflow > 0) top = `${-overflow}px`;
        // Scenario 1 — fits naturally; no inline top needed.
      }

      this.cached = { top, maxHeight, overflowY };
      this.applyCached();
      this.classList.add('is-hovered');
    });
  }

  /**
   * Re-apply cached values after DetailsDisclosure clears the panel's
   * inline style on every CSS transitionend mid-hover.
   * @param {TransitionEvent} evt
   */
  handleTransitionEnd(evt) {
    if (evt.target !== this.panel) return;
    if (this.classList.contains('is-hovered')) this.applyCached();
  }

  /**
   * Handles 'mouseenter' on this <nav-menu>.
   */
  handleMouseEnter() {
    if (!this.isDesktop()) return;
    this.position();
  }

  /**
   * Handles 'mouseleave' on this <nav-menu>.
   */
  handleMouseLeave() {
    this.classList.remove('is-hovered');
  }

  /**
   * Handles 'focusin' on this <nav-menu> (keyboard user tabs into a child).
   */
  handleFocusIn() {
    if (!this.isDesktop()) return;
    this.position();
  }

  /**
   * Handles 'focusout' on this <nav-menu>. Only clear state when focus
   * has actually left the element (relatedTarget is outside).
   * @param {FocusEvent} evt
   */
  handleFocusOut(evt) {
    if (!this.contains(evt.relatedTarget)) {
      this.classList.remove('is-hovered');
    }
  }
}

customElements.define('nav-menu', NavMenuPositioner);

let navMenuResizeTimeout;

window.addEventListener('resize', () => {
  clearTimeout(navMenuResizeTimeout);
  navMenuResizeTimeout = setTimeout(() => {
    document.querySelectorAll('nav-menu.is-hovered').forEach((el) => {
      if (typeof el.position === 'function') el.position();
    });
  }, 100);
});
